/**
 * Theme Manager Component
 * 
 * Allows administrators to manage system color themes.
 * Displays themes grouped by product and attribute, with options to
 * create, edit, delete, and set default themes.
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  SpaceBetween,
  Header,
  Container,
  Box,
  Tabs,
  StatusIndicator,
  Flashbar,
  FlashbarProps,
  Badge,
  ExpandableSection,
} from '@cloudscape-design/components';
import {
  listAdminThemes,
  deleteAdminTheme,
  setDefaultTheme,
  listProducts,
} from '../../services/themeApi';
import { ColorTheme, DataProduct } from '../../types/themes';
import ThemeEditor from './ThemeEditor';

interface ThemeManagerProps {
  onThemesChange?: () => void;
}

const ThemeManager: React.FC<ThemeManagerProps> = ({ onThemesChange }) => {
  const [products, setProducts] = useState<DataProduct[]>([]);
  const [themes, setThemes] = useState<ColorTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [editingTheme, setEditingTheme] = useState<ColorTheme | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [creatingFor, setCreatingFor] = useState<{ productId: string; attributeId: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setFlashbarItems([]);
      const [productsData, themesData] = await Promise.all([
        listProducts(),
        listAdminThemes(),
      ]);
      setProducts(productsData.filter(p => p.is_active));
      setThemes(themesData);
      if (productsData.length > 0 && !selectedProduct) {
        setSelectedProduct(productsData[0].product_id);
      }
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to load data',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTheme = async (themeId: string) => {
    const theme = themes.find(t => t.theme_id === themeId);
    if (theme?.is_default) {
      setFlashbarItems([{
        type: 'error',
        content: 'Cannot delete the default theme. Set another theme as default first.',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
      return;
    }

    if (!confirm('Are you sure you want to delete this theme?')) {
      return;
    }

    try {
      setFlashbarItems([]);
      await deleteAdminTheme(themeId);
      await loadData();
      onThemesChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Theme deleted successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to delete theme',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const handleSetDefault = async (themeId: string) => {
    try {
      setFlashbarItems([]);
      await setDefaultTheme(themeId);
      await loadData();
      onThemesChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Default theme updated successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to set default theme',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const handleCreateTheme = (productId: string, attributeId: string) => {
    setCreatingFor({ productId, attributeId });
    setEditingTheme(null);
    setShowEditor(true);
  };

  const handleEditTheme = (theme: ColorTheme) => {
    setEditingTheme(theme);
    setCreatingFor(null);
    setShowEditor(true);
  };

  const handleEditorClose = async (saved: boolean) => {
    setShowEditor(false);
    setEditingTheme(null);
    setCreatingFor(null);
    if (saved) {
      await loadData();
      onThemesChange?.();
    }
  };

  const getThemesForAttribute = (productId: string, attributeId: string): ColorTheme[] => {
    return themes.filter(
      t => t.data_product === productId && t.attribute === attributeId
    );
  };

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="loading">Loading themes...</StatusIndicator>
        </Box>
      </Container>
    );
  }

  const selectedProductData = products.find(p => p.product_id === selectedProduct);

  return (
    <SpaceBetween size="l">
      <Flashbar items={flashbarItems} />

      <Header variant="h2">System Themes</Header>

      {/* Product Tabs */}
      <Tabs
        activeTabId={selectedProduct || ''}
        onChange={({ detail }) => setSelectedProduct(detail.activeTabId)}
        tabs={products.map((product) => ({
          id: product.product_id,
          label: product.display_name,
          content: (
            <SpaceBetween size="m">
              {product.attributes.filter(attr => attr.is_active).map((attribute) => {
                const attributeThemes = getThemesForAttribute(product.product_id, attribute.attribute_id);
                const defaultTheme = attributeThemes.find(t => t.is_default);

                return (
                  <ExpandableSection
                    key={attribute.attribute_id}
                    headerText={attribute.display_name}
                    headerDescription={`${attribute.data_type}${attribute.unit ? ` (${attribute.unit})` : ''}`}
                    headerActions={
                      <SpaceBetween direction="horizontal" size="xs">
                        {defaultTheme && (
                          <Badge color="green">Default: {defaultTheme.theme_name}</Badge>
                        )}
                        <Button onClick={() => handleCreateTheme(product.product_id, attribute.attribute_id)}>
                          Create Theme
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    <Container>
                      {attributeThemes.length === 0 ? (
                        <Box textAlign="center" color="text-body-secondary" padding="l">
                          No themes configured
                        </Box>
                      ) : (
                        <SpaceBetween size="m">
                          {attributeThemes.map((theme) => (
                            <Container key={theme.theme_id}>
                              <SpaceBetween size="m">
                                <Box>
                                  <SpaceBetween direction="horizontal" size="xs">
                                    <Box variant="h3">{theme.theme_name}</Box>
                                    {theme.is_default && <Badge color="green">Default</Badge>}
                                  </SpaceBetween>
                                  <Box variant="small" color="text-body-secondary">
                                    {theme.mapping_type === 'categorical' ? 'Categorical' : 'Range-based'} •{' '}
                                    {theme.mappings.length} {theme.mappings.length === 1 ? 'mapping' : 'mappings'} •{' '}
                                    v{theme.version}
                                  </Box>
                                </Box>

                                {/* Color Preview */}
                                <Box>
                                  <SpaceBetween direction="horizontal" size="xs">
                                    {theme.mappings.slice(0, 5).map((mapping, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          width: '24px',
                                          height: '24px',
                                          backgroundColor: mapping.color,
                                          border: '1px solid #d5dbdb',
                                          borderRadius: '4px',
                                        }}
                                        title={mapping.label}
                                      />
                                    ))}
                                    {theme.mappings.length > 5 && (
                                      <Box
                                        padding="xxs"
                                        fontSize="body-s"
                                        color="text-body-secondary"
                                      >
                                        +{theme.mappings.length - 5} more
                                      </Box>
                                    )}
                                  </SpaceBetween>
                                </Box>

                                {/* Actions */}
                                <Box float="right">
                                  <SpaceBetween direction="horizontal" size="xs">
                                    <Button onClick={() => handleEditTheme(theme)}>
                                      Edit
                                    </Button>
                                    {!theme.is_default && (
                                      <>
                                        <Button onClick={() => handleSetDefault(theme.theme_id)}>
                                          Set Default
                                        </Button>
                                        <Button onClick={() => handleDeleteTheme(theme.theme_id)}>
                                          Delete
                                        </Button>
                                      </>
                                    )}
                                  </SpaceBetween>
                                </Box>
                              </SpaceBetween>
                            </Container>
                          ))}
                        </SpaceBetween>
                      )}
                    </Container>
                  </ExpandableSection>
                );
              })}
            </SpaceBetween>
          ),
        }))}
      />

      {/* Theme Editor Modal */}
      {showEditor && (
        <ThemeEditor
          theme={editingTheme}
          productId={creatingFor?.productId || editingTheme?.data_product}
          attributeId={creatingFor?.attributeId || editingTheme?.attribute}
          products={products}
          isSystemTheme={true}
          onClose={handleEditorClose}
        />
      )}
    </SpaceBetween>
  );
};

export default ThemeManager;
